"use client"

import { useState, useEffect } from "react" // Import useEffect
import { useNavigate } from "react-router-dom"
import useUserStore from "../store" // Updated import path for the store
import { toast, ToastContainer } from "react-toastify" // Import toast and ToastContainer - CORRECTED IMPORT
import "react-toastify/dist/ReactToastify.css" // Import toast styles

// Import AnimatePresence from framer-motion
import { motion, AnimatePresence } from "framer-motion"

// Add FaArrowLeft to the imports
import {
  FaSearch,
  FaCalendarAlt,
  FaChevronLeft,
  FaChevronRight,
  FaStar,
  FaRegStar,
  FaFileUpload,
  FaSpinner,
  FaCheck,
  FaArrowLeft,
} from "react-icons/fa"

import { format } from "date-fns"

const fadeInVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
}

const cardVariants = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.5 } },
}

const contentTypes = [
  {
    id: "howto",
    name: "How-to Guide",
    desc: "Step-by-step instructions to solve a problem or achieve a goal.",
    icon: "📝",
    category: "How-To & Guides",
    placeholder: "How to Bake a Perfect Chocolate Cake",
  },
  {
    id: "versus",
    name: "Versus Article",
    desc: "Side-by-side comparison of two or more products, services, or ideas.",
    icon: "⚔️",
    category: "Comparisons & Reviews",
    placeholder: "Cloud Hosting vs Dedicated Servers: Which is Right for You?",
  },
  {
    id: "review",
    name: "Review",
    desc: "In-depth evaluation and critique of a product, service, or book.",
    icon: "⭐",
    category: "Comparisons & Reviews",
    placeholder: "Apple Watch Series 9 Review: Is It Worth the Upgrade?",
  },
  {
    id: "tutorial",
    name: "Tutorial",
    desc: "Detailed, hands-on guide to teach a specific skill or process.",
    icon: "💻",
    category: "How-To & Guides",
    placeholder: "Beginner's Tutorial to Python Programming",
  },
  {
    id: "listicle",
    name: "Listicle",
    desc: "Article presented in the form of a numbered or bulleted list.",
    icon: "📋",
    category: "Lists & Collections",
    placeholder: "10 Must-Visit Beaches in Bali",
  },
  {
    id: "news",
    name: "News Analysis",
    desc: "Breakdown and interpretation of current events or industry trends.",
    icon: "📰",
    category: "News & Opinions",
    placeholder: "Analyzing the Impact of AI on the Job Market",
  },
  {
    id: "opinion",
    name: "Opinion Article",
    desc: "Personal viewpoint or argument on a topical issue.",
    icon: "🗣️",
    category: "News & Opinions",
    placeholder: "Why Remote Work is the Future of Employment",
  },
  {
    id: "essay",
    name: "Educational Essay",
    desc: "In-depth exploration of a topic, aiming to educate and inform.",
    icon: "📚",
    category: "How-To & Guides",
    placeholder: "The History of the Internet and Its Impact on Society",
  },
  {
    id: "checklist",
    name: "Checklist",
    desc: "Actionable list of items to ensure completion or accuracy.",
    icon: "✅",
    category: "How-To & Guides",
    placeholder: "Pre-Travel Checklist: Ensure a Smooth Trip",
  },
  {
    id: "qa",
    name: "Q&A Article",
    desc: "Questions and answers format to address common queries on a subject.",
    icon: "❓",
    category: "Lists & Collections",
    placeholder: "Your Top Questions About Starting a Business Answered",
  },
  {
    id: "roundup",
    name: "Roundup",
    desc: "Collection of the best examples, resources, or products in a category.",
    icon: "🔄",
    category: "Lists & Collections",
    placeholder: "Best Productivity Apps for 2024 - Our Top Picks",
  },
  {
    id: "ultimate",
    name: "Ultimate Guide",
    desc: "Comprehensive and definitive resource on a specific topic.",
    icon: "🚀",
    category: "How-To & Guides",
    placeholder: "The Ultimate Guide to Digital Marketing for Beginners",
  },
]

// Add a new ProgressIndicator component before the ArticleCreator component
function ProgressIndicator({ currentStep, totalSteps }) {
  return (
    <div className="flex items-center justify-center mb-8">
      {Array.from({ length: totalSteps }).map((_, index) => (
        <div key={index} className="flex items-center">
          <div
            className={`w-8 h-8 rounded-full flex items-center justify-center ${
              index < currentStep
                ? "bg-gradient-to-r from-orange-500 to-rose-500"
                : index === currentStep
                  ? "bg-gradient-to-r from-orange-500 to-rose-500 ring-4 ring-orange-500/20"
                  : "bg-gray-700"
            }`}
          >
            {index < currentStep ? (
              <FaCheck className="text-white text-sm" />
            ) : (
              <span className="text-white text-sm">{index + 1}</span>
            )}
          </div>
          {index < totalSteps - 1 && (
            <div
              className={`h-1 w-12 ${
                index < currentStep ? "bg-gradient-to-r from-orange-500 to-rose-500" : "bg-gray-700"
              }`}
            />
          )}
        </div>
      ))}
    </div>
  )
}

// Update the SimpleCalendar component to add accessibility attributes
function SimpleCalendar({ selectedDate, onDateSelect, disabledDates }) {
  const [currentMonth, setCurrentMonth] = useState(new Date())

  const getDaysInMonth = (year, month) => {
    return new Date(year, month + 1, 0).getDate()
  }

  const getFirstDayOfMonth = (year, month) => {
    return new Date(year, month, 1).getDay()
  }

  const year = currentMonth.getFullYear()
  const month = currentMonth.getMonth()
  const daysInMonth = getDaysInMonth(year, month)
  const firstDayOfMonth = getFirstDayOfMonth(year, month)

  const prevMonth = () => {
    setCurrentMonth(new Date(year, month - 1, 1))
  }

  const nextMonth = () => {
    setCurrentMonth(new Date(year, month + 1, 1))
  }

  const isDateDisabled = (date) => {
    return disabledDates.some(
      (disabledDate) =>
        disabledDate.getDate() === date.getDate() &&
        disabledDate.getMonth() === date.getMonth() &&
        disabledDate.getFullYear() === date.getFullYear(),
    )
  }

  const isDateSelected = (date) => {
    return (
      selectedDate &&
      selectedDate.getDate() === date.getDate() &&
      selectedDate.getMonth() === date.getMonth() &&
      selectedDate.getFullYear() === date.getFullYear()
    )
  }

  const days = []
  for (let i = 0; i < firstDayOfMonth; i++) {
    days.push(<div key={`empty-${i}`} className="h-10 w-10"></div>)
  }

  for (let day = 1; day <= daysInMonth; day++) {
    const date = new Date(year, month, day)
    const disabled = isDateDisabled(date)
    const selected = isDateSelected(date)

    days.push(
      <button
        key={day}
        onClick={() => !disabled && onDateSelect(date)}
        disabled={disabled}
        aria-label={`Select ${format(date, "MMMM d, yyyy")}`}
        aria-disabled={disabled}
        aria-selected={selected}
        className={`h-10 w-10 rounded-full flex items-center justify-center transition-colors ${
          selected
            ? "bg-gradient-to-r from-orange-500 to-rose-500 text-white"
            : disabled
              ? "text-gray-600 cursor-not-allowed"
              : "text-white hover:bg-[#32364A]"
        }`}
      >
        {day}
      </button>,
    )
  }

  const weekdays = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"]

  return (
    <div className="bg-[#2A2D38] rounded-lg p-4 border border-gray-700 w-full shadow-lg">
      <div className="flex justify-between items-center mb-4">
        <button
          type="button"
          onClick={prevMonth}
          className="text-gray-400 hover:text-white p-2 rounded-full hover:bg-gray-700"
          aria-label="Previous month"
        >
          <FaChevronLeft />
        </button>
        <h3 className="text-white font-medium">{format(currentMonth, "MMMM yyyy")}</h3>
        <button
          type="button"
          onClick={nextMonth}
          className="text-gray-400 hover:text-white p-2 rounded-full hover:bg-gray-700"
          aria-label="Next month"
        >
          <FaChevronRight />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1 mb-2">
        {weekdays.map((day) => (
          <div key={day} className="h-8 flex items-center justify-center text-gray-400 text-sm">
            {day}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1">{days}</div>
    </div>
  )
}

// Add a getCurrentStepNumber function to the ArticleCreator component
export default function ArticleCreator() {
  const [step, setStep] = useState("type")
  const [articleType, setArticleType] = useState("")
  const [formData, setFormData] = useState({ title: "", description: "" }) // Initialize title and description in formData
  const [searchTerm, setSearchTerm] = useState("")
  const [selectedCategory, setSelectedCategory] = useState("All")
  const [showAITools, setShowAITools] = useState(false)
  const [date, setDate] = useState()
  const [showCalendar, setShowCalendar] = useState(false)
  const [error, setError] = useState(null)
  const navigate = useNavigate()
  const blogs = useUserStore((state) => state.blogs)
  const blogId = blogs && blogs.length > 0 ? blogs[0].id : null
  const [isUploadMode, setIsUploadMode] = useState(false) // New state for upload mode
  const [uploadedFile, setUploadedFile] = useState(null) // State to store uploaded file content
  const [uploadedFileName, setUploadedFileName] = useState("") // State to store uploaded file name
  const [isLoading, setIsLoading] = useState(false) // New loading state
  const [isFormValid, setIsFormValid] = useState(false) // New form validity state - ADDED
  const [uploadedDraftId, setUploadedDraftId] = useState(null) // State to store draft ID from upload

  const disabledDates = [new Date(2024, 2, 5), new Date(2024, 2, 10), new Date(2024, 2, 15)]

  const categories = ["All", "How-To & Guides", "Comparisons & Reviews", "News & Opinions", "Lists & Collections"]

  const filteredTypes = contentTypes.filter((type) => {
    const matchesSearch =
      type.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      type.desc.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesCategory = selectedCategory === "All" || type.category === selectedCategory
    return matchesSearch && matchesCategory
  })

  // Add this function after the filteredTypes constant
  const getCurrentStepNumber = () => {
    if (step === "type") return 0
    if (step === "setup" || step === "upload-setup") return 1
    return 0
  }

  const handleTypeSelect = (type) => {
    setArticleType(type)
    setStep("setup")
    setIsUploadMode(false) // Ensure upload mode is off when type is selected
    setUploadedDraftId(null) // Reset draft ID when switching to type selection
  }

  const handleSetupSubmit = async (e) => {
    e.preventDefault()
    setError(null)

    if (!blogId) {
      setError("Blog ID is not available. Please make sure a blog is selected.")
      return
    }

    const setupData = {
      title: formData.title, // Use formData.title
      pitch: formData.description, // Use formData.description
      date: date ? format(date, "yyyy-MM-dd") : null,
      type: isUploadMode
        ? "Uploaded Article"
        : articleType
          ? contentTypes.find((type) => type.id === articleType)?.name
          : "", // Set type for uploaded articles
      uploadedFileContent: uploadedFile, // Include uploaded file content
      uploadedFileName: uploadedFileName, // Include uploaded file name
      status: isUploadMode ? 1 : 0,
      draft_id: uploadedDraftId, // Include draftId in the data
    }

    // Type specific data is now empty for ALL types in step 2
    const typeSpecificData = {}

    const combinedData = { ...setupData, ...typeSpecificData }

    // Determine status and draft_id based on upload mode
    let apiStatus = 0 // Default status for new articles
    let draftIdForApi = null

    if (isUploadMode) {
      apiStatus = 1 // Status for uploaded articles
      draftIdForApi = uploadedDraftId // Use uploaded draft ID if available
    }

    combinedData.status = apiStatus
    if (draftIdForApi) {
      combinedData.draft_id = draftIdForApi
    }

    console.log("Article Setup Data:", combinedData)

    try {
      const response = await fetch(`https://trendhopper-c3934b7293e6.herokuapp.com/create_or_update_draft/${blogId}/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(combinedData),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || `Failed to create draft. Status: ${response.status}`)
      }

      const data = await response.json()
      console.log("Success:", data)
      if (data.success) {
        toast.success("Article successfully created!")

        // Introduce a Promise-based delay
        await new Promise((resolve) => setTimeout(resolve, 500)) // Wait for 3 seconds

        navigate("/articles-table-view") // Navigate after the delay
      } else {
        setError(data.message || "Failed to create draft.")
      }
    } catch (error) {
      console.error("Error:", error)
      setError(error.message || "An unexpected error occurred.")
    }

    setFormData({ title: "", description: "" }) // Clear form data after submission
    setStep("type")
    setIsUploadMode(false) // Reset upload mode after submission
    setUploadedFile(null) // Clear uploaded file after submission
    setUploadedFileName("") // Clear uploaded file name
    setUploadedDraftId(null) // Clear uploaded draft ID after submission
  }

  const handleBack = () => {
    if (step === "setup" || step === "upload-setup") setStep("type")
    if (isUploadMode) setIsUploadMode(false) // Exit upload mode if going back from upload section
    if (step === "upload-setup") {
      setUploadedFile(null) // Clear uploaded file if going back from upload setup
      setUploadedFileName("") // Clear uploaded file name
      setUploadedDraftId(null) // Clear draft ID if going back from upload setup
      setFormData({ ...formData, title: "", description: "" }) // Clear title and description when going back from upload setup
    }
  }

  const getWordCount = (text) => {
    return text?.split(/\s+/).filter(Boolean).length || 0
  }

  const getReadingTime = (text) => {
    const words = getWordCount(text)
    const minutes = Math.ceil(words / 200)
    return minutes
  }

  const selectedContentType = contentTypes.find((type) => type.id === articleType)

  const renderStars = (rating) => {
    const stars = []
    const ratingNum = Number.parseInt(rating || 0)

    for (let i = 1; i <= 5; i++) {
      if (i <= ratingNum) {
        stars.push(<FaStar key={i} className="text-yellow-500" />)
      } else {
        stars.push(<FaRegStar key={i} className="text-yellow-500" />)
      }
    }

    return (
      <div className="flex items-center">
        {stars}
        <span className="ml-2 text-white">{rating}/5</span>
      </div>
    )
  }

  const handleFileUpload = async (event) => {
    const file = event.target.files[0]
    if (file) {
      setIsLoading(true)
      setError(null) // Clear any previous errors

      const formData = new FormData()
      formData.append("document", file)
      formData.append("blogId", blogId)

      try {
        const response = await fetch("https://trendhopper-c3934b7293e6.herokuapp.com/upload_article/", {
          // Replace with your actual URL
          method: "POST",
          body: formData,
        })

        console.log("response from upload article", response)
        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(
            errorData.errors ? errorData.errors.join(", ") : `HTML validation failed. Status: ${response.status}`,
          )
        }

        const data = await response.json()

        if (data.valid) {
          // HTML is valid, proceed to next step
          const reader = new FileReader()
          reader.onload = (e) => {
            setUploadedFile(e.target.result) // Store file content as string
            setUploadedFileName(file.name) // Store file name
            // **START - Update formData with title and description from response**
            setFormData({
              title: data.title || "", // Use title from response or default to empty string
              description: data.description || "", // Use description from response or default to empty string
            })
            setUploadedDraftId(data.draft_id) // Store draft ID from response
            // **END - Update formData with title and description from response**
            setStep("upload-setup") // Move to upload setup step on successful upload
            setIsLoading(false) // Stop loading spinner
          }
          reader.onerror = (e) => {
            setError("Error reading file content after validation.")
            console.error("File reading error after validation:", e)
            setUploadedFile(null)
            setUploadedFileName("")
            setUploadedDraftId(null) // Clear draft ID on error
            setIsLoading(false)
          }
          reader.readAsText(file)
        } else {
          // HTML is invalid, display errors
          setIsLoading(false)
          const errorMessages = data.errors.join(", ")
          toast.error(`Invalid HTML: ${errorMessages}`)
          setUploadedFile(null) // Clear uploaded file state
          setUploadedFileName("") // Clear uploaded file name
          setUploadedDraftId(null) // Clear draft ID on invalid HTML
        }
      } catch (fetchError) {
        setIsLoading(false)
        setError(`Error validating HTML: ${fetchError.message}`)
        toast.error(`Error validating HTML: ${fetchError.message}`)
        console.error("Fetch error during HTML validation:", fetchError)
        setUploadedFile(null) // Clear uploaded file state
        setUploadedFileName("") // Clear uploaded file name
        setUploadedDraftId(null) // Clear draft ID on fetch error
      }
    } else {
      setUploadedFile(null)
      setUploadedFileName("")
      setUploadedDraftId(null) // Clear draft ID if no file selected
    }
  }

  // Validation function - ADDED
  const validateForm = (data) => {
    return !!date && data.title && data.description // Check if date, title and description are filled
  }

  // useEffect to update form validity when form data or date changes - ADDED
  useEffect(() => {
    setIsFormValid(validateForm(formData))
  }, [date, formData])

  const handleInputChange = (e) => {
    // ADDED
    setFormData({ ...formData, [e.target.name]: e.target.value })
  }

  // Update the return statement to include the ProgressIndicator and improve the UI
  return (
    <div className="min-h-screen mb-3 bg-[#1A1D25]">
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="dark"
      />

      <motion.div
        className="mx-auto bg-[#1A202C] min-h-[800px] rounded-xl shadow-xl p-6 md:p-8 relative z-10"
        initial="hidden"
        animate="visible"
        
        variants={fadeInVariants}
      >
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-3xl font-bold">
            <span className="bg-gradient-to-r from-orange-500 to-rose-500 bg-clip-text text-transparent">
              Create Your Article
            </span>
          </h1>
          <p className="text-gray-400 mt-2">
            {step === "type"
              ? "Choose a content type or upload your article"
              : step === "setup"
                ? "Set up your article details"
                : step === "upload-setup"
                  ? "Confirm details for your uploaded article"
                  : ""}
          </p>
        </div>

        {/* Progress indicator */}
        <ProgressIndicator currentStep={getCurrentStepNumber()} totalSteps={2} />

        {/* Error message - improved styling */}
        {error && (
          <div className="bg-red-900/30 border border-red-500 text-red-200 p-4 rounded-lg mb-6 flex items-start">
            <div className="mr-3 mt-0.5">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div>
              <h3 className="font-medium">Error</h3>
              <p>{error}</p>
            </div>
          </div>
        )}

        <AnimatePresence mode="wait">
          {/* Step 1: Type Selection */}
          {step === "type" && (
            <motion.div
              key="type-step"
              initial="hidden"
              animate="visible"
              exit={{ opacity: 0, y: -20, transition: { duration: 0.3 } }}
              variants={fadeInVariants}
            >
              {/* Type Selection or Upload Toggle - improved styling */}
              <div className="mb-6 flex justify-center gap-4">
                <button
                  onClick={() => setIsUploadMode(false)}
                  className={`px-6 py-3 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                    !isUploadMode
                      ? "bg-gradient-to-r from-orange-500 to-rose-500 text-white shadow-lg shadow-orange-500/20"
                      : "bg-[#2A2D38] text-gray-400 hover:text-white hover:bg-[#32364A]"
                  }`}
                  aria-pressed={!isUploadMode}
                >
                  Choose Type
                </button>
                <button
                  onClick={() => setIsUploadMode(true)}
                  className={`px-6 py-3 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                    isUploadMode
                      ? "bg-gradient-to-r from-orange-500 to-rose-500 text-white shadow-lg shadow-orange-500/20"
                      : "bg-[#2A2D38] text-gray-400 hover:text-white hover:bg-[#32364A]"
                  }`}
                  disabled={isLoading}
                  aria-pressed={isUploadMode}
                >
                  Upload File
                </button>
              </div>

              {/* Upload File Section - improved styling */}
              {isUploadMode && (
                <motion.div className="mb-8 p-6 border border-gray-700 bg-[#2A2D38] rounded-lg" variants={cardVariants}>
                  <div className="space-y-4">
                    <label htmlFor="article-upload" className="block text-sm font-medium text-gray-300">
                      Upload Article File
                    </label>
                    <div className="mt-1 flex justify-center px-6 pt-8 pb-8 border-2 border-dashed rounded-lg border-gray-700 relative">
                      <div className="space-y-3 text-center">
                        <FaFileUpload className="mx-auto h-12 w-12 text-gray-400" />
                        <div className="flex flex-col items-center text-sm text-gray-400">
                          <p className="mb-2">Drag and drop your file here, or</p>
                          <label
                            htmlFor="article-upload"
                            className="relative cursor-pointer bg-gray-800 rounded-md font-medium text-orange-500 hover:text-orange-400 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-orange-500 px-4 py-2 transition-colors"
                          >
                            <span>Browse files</span>
                            <input
                              id="article-upload"
                              name="article-upload"
                              type="file"
                              className="sr-only"
                              onChange={handleFileUpload}
                              accept=".pdf, .html"
                              aria-label="Upload article file"
                            />
                          </label>
                        </div>
                        <p className="text-xs text-gray-500">PDF, HTML up to 10MB</p>
                        {uploadedFile && !isLoading && !error && (
                          <div className="flex items-center justify-center text-green-500 text-sm mt-2">
                            <FaCheck className="mr-2" />
                            <span>File uploaded successfully!</span>
                          </div>
                        )}
                      </div>
                      {isLoading && (
                        <div className="absolute inset-0 bg-black bg-opacity-70 flex justify-center items-center rounded-lg backdrop-blur-sm">
                          <div className="flex flex-col items-center">
                            <FaSpinner className="animate-spin text-orange-500 text-3xl mb-2" />
                            <p className="text-white">Processing file...</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Content Type Grid - improved styling and accessibility */}
              {!isUploadMode && (
                <>
                  {/* Search and Filter */}
                  <div className="mb-6 space-y-4">
                    <div className="flex flex-col md:flex-row gap-4 justify-between">
                      <div className="relative flex-1">
                        <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        <input
                          type="text"
                          placeholder="Search content types..."
                          className="w-full pl-10 pr-4 py-3 bg-[#2A2D38] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 focus:outline-none"
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          aria-label="Search content types"
                        />
                      </div>
                      <div className="flex gap-2 overflow-x-auto pb-2 md:pb-0">
                        {categories.map((category) => (
                          <button
                            key={category}
                            onClick={() => setSelectedCategory(category)}
                            className={`px-4 py-3 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                              selectedCategory === category
                                ? "bg-gradient-to-r from-orange-500 to-rose-500 text-white"
                                : "bg-[#2A2D38] text-gray-400 hover:text-white hover:bg-[#32364A]"
                            }`}
                            aria-pressed={selectedCategory === category}
                          >
                            {category}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Content Type Grid - add empty state */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {filteredTypes.length > 0 ? (
                      filteredTypes.map((type) => (
                        <motion.div
                          key={type.id}
                          onClick={() => handleTypeSelect(type.id)}
                          className="border border-gray-700 bg-[#2A2D38] rounded-lg p-6 cursor-pointer hover:bg-[#32364A] hover:shadow-lg transition-all duration-200"
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          tabIndex={0}
                          role="button"
                          aria-label={`Select ${type.name}`}
                          onKeyDown={(e) => e.key === "Enter" && handleTypeSelect(type.id)}
                        >
                          <div className="flex items-start space-x-4">
                            <span className="text-2xl">{type.icon}</span>
                            <div>
                              <div className="flex items-center gap-2 flex-wrap">
                                <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-orange-500">
                                  {type.name}
                                </h3>
                                <span className="text-xs bg-gray-800 text-gray-300 px-2 py-1 rounded">
                                  {type.category}
                                </span>
                              </div>
                              <p className="text-gray-400">{type.desc}</p>
                            </div>
                          </div>
                        </motion.div>
                      ))
                    ) : (
                      <div className="col-span-2 text-center py-12 text-gray-400">
                        <FaSearch className="mx-auto h-12 w-12 mb-4 opacity-50" />
                        <p className="text-lg">No content types found matching your search</p>
                        <p className="mt-2">Try adjusting your search terms or filters</p>
                      </div>
                    )}
                  </div>
                </>
              )}
            </motion.div>
          )}

          {/* Step 2: Setup Form - improved with back button and content type info */}
          {step === "setup" && selectedContentType && (
            <motion.div
              key="setup-step"
              initial="hidden"
              animate="visible"
              exit={{ opacity: 0, y: -20, transition: { duration: 0.3 } }}
              variants={fadeInVariants}
              className="max-w-2xl mx-auto"
            >
              <form onSubmit={handleSetupSubmit} className="space-y-6">
                {/* Back button */}
                <button
                  type="button"
                  onClick={handleBack}
                  className="mb-4 flex items-center text-gray-400 hover:text-white transition-colors"
                  aria-label="Go back to content type selection"
                >
                  <FaArrowLeft className="mr-2" />
                  <span>Back to content types</span>
                </button>

                {/* Content type info */}
                <div className="flex items-center gap-3 p-4 bg-[#2A2D38] rounded-lg border border-gray-700 mb-6">
                  <span className="text-2xl">{selectedContentType.icon}</span>
                  <div>
                    <h3 className="font-medium text-white">{selectedContentType.name}</h3>
                    <p className="text-sm text-gray-400">{selectedContentType.desc}</p>
                  </div>
                </div>

                {/* Article Title - improved with required indicator */}
                <div className="space-y-2">
                  <label htmlFor="title" className="text-sm font-medium text-gray-300 flex items-center">
                    Article Title
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  <input
                    id="title"
                    name="title"
                    required
                    className="w-full p-3 bg-[#2A2D38] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 focus:outline-none"
                    placeholder={selectedContentType.placeholder}
                    value={formData.title}
                    onChange={handleInputChange}
                    aria-required="true"
                  />
                </div>

                {/* Article Description - improved with required indicator */}
                <div className="space-y-2">
                  <label htmlFor="description" className="text-sm font-medium text-gray-300 flex items-center">
                    Brief Description
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    required
                    rows={4}
                    className="w-full p-3 bg-[#2A2D38] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 focus:outline-none resize-none"
                    placeholder="Provide a brief description of what your article will cover..."
                    value={formData.description}
                    onChange={handleInputChange}
                    aria-required="true"
                  />
                  <p className="text-xs text-gray-500">This helps in generating better content and improving SEO.</p>
                </div>

                {/* Publish Date - improved with required indicator */}
                <div className="space-y-2">
                  <label htmlFor="publish-date" className="text-sm font-medium text-gray-300 flex items-center">
                    Publish Date
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  <div className="relative">
                    <button
                      id="publish-date"
                      type="button"
                      onClick={() => setShowCalendar(!showCalendar)}
                      className="w-full p-3 bg-[#2A2D38] border border-gray-700 rounded-lg text-left flex items-center justify-between text-white focus:border-orange-500 focus:ring-1 focus:ring-orange-500 focus:outline-none"
                      aria-haspopup="true"
                      aria-expanded={showCalendar}
                    >
                      <span>{date ? format(date, "MMMM d, yyyy") : "Select a date"}</span>
                      <FaCalendarAlt className="text-gray-400" />
                    </button>

                    {showCalendar && (
                      <div className="absolute z-10 mt-2 w-full">
                        <SimpleCalendar
                          selectedDate={date}
                          onDateSelect={(newDate) => {
                            setDate(newDate)
                            setShowCalendar(false)
                          }}
                          disabledDates={disabledDates}
                        />
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-gray-500">Some dates may be unavailable for scheduling.</p>
                </div>

                {/* Action Buttons - improved styling and loading state */}
                <div className="flex gap-3 pt-6">
                  <button
                    type="button"
                    onClick={handleBack}
                    className="flex-1 px-4 py-3 bg-[#2A2D38] text-white rounded-lg hover:bg-[#32364A] transition-colors"
                  >
                    Back
                  </button>
                  <button
                    type="submit"
                    disabled={!isFormValid || isLoading}
                    className={`flex-1 py-3 px-4 rounded-lg transition-colors duration-300 flex items-center justify-center gap-2 ${
                      isFormValid && !isLoading
                        ? "bg-gradient-to-r from-orange-500 to-rose-500 text-white hover:from-orange-600 hover:to-rose-600 shadow-lg shadow-orange-500/20"
                        : "bg-gray-600 text-gray-300 cursor-not-allowed"
                    }`}
                    aria-busy={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <FaSpinner className="animate-spin" />
                        <span>Processing...</span>
                      </>
                    ) : (
                      <>
                        <FaCheck />
                        <span>Create Article</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </motion.div>
          )}

          {/* Step 2 (Upload): Upload Setup Form - improved with back button and file info */}
          {step === "upload-setup" && (
            <motion.div
              key="upload-setup-step"
              initial="hidden"
              animate="visible"
              exit={{ opacity: 0, y: -20, transition: { duration: 0.3 } }}
              variants={fadeInVariants}
              className="max-w-2xl mx-auto"
            >
              <form onSubmit={handleSetupSubmit} className="space-y-6">
                {/* Back button */}
                <button
                  type="button"
                  onClick={handleBack}
                  className="mb-4 flex items-center text-gray-400 hover:text-white transition-colors"
                  aria-label="Go back to file upload"
                >
                  <FaArrowLeft className="mr-2" />
                  <span>Back to file upload</span>
                </button>

                {/* Upload info */}
                <div className="flex items-center gap-3 p-4 bg-[#2A2D38] rounded-lg border border-gray-700 mb-6">
                  <FaFileUpload className="text-2xl text-orange-500" />
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-white">Uploaded File</h3>
                    <p className="text-sm text-gray-400 truncate">{uploadedFileName}</p>
                  </div>
                  <span className="bg-green-900/30 text-green-400 text-xs px-2 py-1 rounded-full border border-green-500/30">
                    Validated
                  </span>
                </div>

                {/* Article Title - improved with required indicator */}
                <div className="space-y-2">
                  <label htmlFor="upload-title" className="text-sm font-medium text-gray-300 flex items-center">
                    Article Title
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  <input
                    id="upload-title"
                    name="title"
                    required
                    className="w-full p-3 bg-[#2A2D38] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 focus:outline-none"
                    placeholder="Enter article title"
                    value={formData.title || ""}
                    onChange={handleInputChange}
                    aria-required="true"
                  />
                </div>

                {/* Article Description - improved with required indicator */}
                <div className="space-y-2">
                  <label htmlFor="upload-description" className="text-sm font-medium text-gray-300 flex items-center">
                    Brief Description
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  <textarea
                    id="upload-description"
                    name="description"
                    required
                    rows={4}
                    className="w-full p-3 bg-[#2A2D38] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 focus:outline-none resize-none"
                    placeholder="Provide a brief description of what your article covers..."
                    value={formData.description || ""}
                    onChange={handleInputChange}
                    aria-required="true"
                  />
                  <p className="text-xs text-gray-500">This helps in organizing your articles.</p>
                </div>

                {/* Publish Date - improved with required indicator */}
                <div className="space-y-2">
                  <label htmlFor="upload-publish-date" className="text-sm font-medium text-gray-300 flex items-center">
                    Publish Date
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  <div className="relative">
                    <button
                      id="upload-publish-date"
                      type="button"
                      onClick={() => setShowCalendar(!showCalendar)}
                      className="w-full p-3 bg-[#2A2D38] border border-gray-700 rounded-lg text-left flex items-center justify-between text-white focus:border-orange-500 focus:ring-1 focus:ring-orange-500 focus:outline-none"
                      aria-haspopup="true"
                      aria-expanded={showCalendar}
                    >
                      <span>{date ? format(date, "MMMM d, yyyy") : "Select a date"}</span>
                      <FaCalendarAlt className="text-gray-400" />
                    </button>

                    {showCalendar && (
                      <div className="absolute z-10 mt-2 w-full">
                        <SimpleCalendar
                          selectedDate={date}
                          onDateSelect={(newDate) => {
                            setDate(newDate)
                            setShowCalendar(false)
                          }}
                          disabledDates={disabledDates}
                        />
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-gray-500">Some dates may be unavailable for scheduling.</p>
                </div>

                {/* Action Buttons - improved styling and loading state */}
                <div className="flex gap-3 pt-6">
                  <button
                    type="button"
                    onClick={handleBack}
                    className="flex-1 px-4 py-3 bg-[#2A2D38] text-white rounded-lg hover:bg-[#32364A] transition-colors"
                  >
                    Back
                  </button>
                  <button
                    type="submit"
                    disabled={!isFormValid || isLoading}
                    className={`flex-1 py-3 px-4 rounded-lg transition-colors duration-300 flex items-center justify-center gap-2 ${
                      isFormValid && !isLoading
                        ? "bg-gradient-to-r from-orange-500 to-rose-500 text-white hover:from-orange-600 hover:to-rose-600 shadow-lg shadow-orange-500/20"
                        : "bg-gray-600 text-gray-300 cursor-not-allowed"
                    }`}
                    aria-busy={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <FaSpinner className="animate-spin" />
                        <span>Processing...</span>
                      </>
                    ) : (
                      <>
                        <FaCheck />
                        <span>Import Article</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  )
}

